import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, X, RefreshCw, Check, AlertTriangle, CircleDot, CircleAlert, Clock } from 'lucide-react';
import { EntityLink } from '../../../components/shared/EntityLink';

export function VerificationModal({ data, onClose, onRefresh }: { data: any; onClose: () => void; onRefresh: () => void }) {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<'verified' | 'pending' | 'issues'>(
    data.pending_count > 0 ? 'pending' : data.issue_count > 0 ? 'issues' : 'verified'
  );

  const sections = [
    { key: 'verified' as const, label: t('wh.verifiedItems'), count: data.verified_count, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30', icon: Check },
    { key: 'pending' as const, label: t('wh.pendingItems'), count: data.pending_count, color: 'text-yellow-600', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30', icon: CircleDot },
    { key: 'issues' as const, label: t('wh.issueItems'), count: data.issue_count, color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30', icon: CircleAlert },
  ];

  const currentItems = activeSection === 'verified' ? data.verified : activeSection === 'pending' ? data.pending : data.issues;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--color-bg-card)] rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-3">
            <ShieldCheck size={20} className="text-emerald-600" />
            <div>
              <h2 className="text-lg font-bold text-[var(--color-text)]">{t('wh.verification')}</h2>
              <p className="text-xs text-[var(--color-text-secondary)]">
                Batch #{data.seller_batch_num ?? data.batch_id} -- {new Date(data.batch_date).toLocaleDateString()} -- {data.total} items
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onRefresh} className="p-2 rounded-lg hover:bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)]"><RefreshCw size={16} /></button>
            <button onClick={onClose} className="p-1 hover:bg-[var(--color-bg-hover)] rounded"><X size={18} className="text-[var(--color-text-secondary)]" /></button>
          </div>
        </div>

        <div className={`mx-4 mt-3 p-3 rounded-lg flex items-center gap-3 ${
          data.all_clear ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700'
            : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700'
        }`}>
          {data.all_clear ? (
            <><Check size={20} className="text-green-600 shrink-0" /><span className="text-sm font-medium text-green-800 dark:text-green-300">{t('wh.allClear')}</span></>
          ) : (
            <><AlertTriangle size={20} className="text-yellow-600 shrink-0" />
              <div className="text-sm">
                {data.pending_count > 0 && <span className="font-medium text-yellow-800 dark:text-yellow-300">{data.pending_count} {t('wh.hasPending')}</span>}
                {data.pending_count > 0 && data.issue_count > 0 && <span className="text-yellow-600"> -- </span>}
                {data.issue_count > 0 && <span className="font-medium text-red-700 dark:text-red-300">{data.issue_count} {t('wh.hasIssues')}</span>}
              </div>
            </>
          )}
        </div>

        <div className="flex gap-2 px-4 mt-3">
          {sections.map(s => (
            <button key={s.key} onClick={() => setActiveSection(s.key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeSection === s.key ? `${s.bgColor} ${s.color}` : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
              }`}>
              <s.icon size={14} />{s.label}
              <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${activeSection === s.key ? s.color : 'text-[var(--color-text-secondary)]'}`}>{s.count}</span>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {currentItems.length === 0 ? (
            <div className="text-center py-8 text-[var(--color-text-secondary)]">
              <p className="text-sm">Sin items en esta categoria</p>
            </div>
          ) : (
            <div className="space-y-2">
              {currentItems.map((item: any, i: number) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${
                  activeSection === 'verified' ? 'bg-green-50/50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                  : activeSection === 'pending' ? 'bg-yellow-50/50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-800'
                  : 'bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
                }`}>
                  {item.thumbnail ? (
                    <img src={item.thumbnail} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                  ) : (
                    <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[var(--color-text)]">{item.display_title || item.title || item.asin || '-'}</p>
                    <div className="flex items-center gap-3 text-xs text-[var(--color-text-secondary)]">
                      {item.ml_order_id && <EntityLink type="ml_order" value={item.ml_order_id} />}
                      {item.asin && <EntityLink type="asin" value={item.asin} />}
                      {item.buyer_nickname && <span>{item.buyer_nickname}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {activeSection === 'verified' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                        <Check size={12} />{item.verify_status === 'dropped_off' ? 'Drop-off' : item.verify_status?.replace(/_/g, ' ')}
                      </span>
                    )}
                    {activeSection === 'pending' && (
                      <div>
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">
                          <Clock size={12} />Pendiente
                        </span>
                        {item.reason && <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">{item.reason}</p>}
                      </div>
                    )}
                    {activeSection === 'issues' && (
                      <div>
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                          <CircleAlert size={12} />{item.verify_status === 'cancelled' ? 'Cancelado' : item.verify_status}
                        </span>
                        {item.reason && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{item.reason}</p>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[var(--color-border)] flex items-center justify-between">
          <div className="text-sm text-[var(--color-text-secondary)]">{t('wh.verifyDesc')}</div>
          <div className="flex items-center gap-3 text-sm font-medium">
            <span className="text-green-600">{data.verified_count} OK</span>
            {data.pending_count > 0 && <span className="text-yellow-600">{data.pending_count} ...</span>}
            {data.issue_count > 0 && <span className="text-red-600">{data.issue_count} X</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
